import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, CheckBox, DataTable, Diagram, FormField, Grid, Heading, List, Stack, Text, Image } from "grommet";
import { get } from "lodash";
import React, { useEffect } from "react";

import { Routes, Route } from "react-router-dom";
import { useUser } from "../account/AccountStore";
import SkeletonWrapper from "../components/SkeletonWrapper";
import StyledModal from "../components/StyledModel";
import {
    ChannelType,
    createCategory,
    CreateCategoryRequest,
    createChannels,
    CreateChannelsRequest,
    DiscordChannel,
    DiscordGuild,
    DiscordRole,
    DiscordServerRole,
    DiscordServerViewModel,
    getOrganizationGuild,
    getOrganizationServer,
    LinkCategoryRequest,
    linkExistingCategory,
    linkExistingRole,
    LinkRoleRequest,
    listCategories,
    listChannels,
    listRoles,
    removeOrganizationServer,
    RemoveRoleRequest,
    removeServerRole,
} from "./DiscordStore";

const DiscordServerManagement = () => {
    const [server, setServer] = React.useState({} as DiscordServerViewModel);
    const [guild, setGuild] = React.useState({} as DiscordGuild);
    const [showLink, setShowLink] = React.useState(false);
    const [categories, setCategories] = React.useState([] as DiscordChannel[]);
    const [channels, setChannels] = React.useState([] as DiscordChannel[]);
    const [allChannels, setAllChannels] = React.useState([] as DiscordChannel[]);

    const [channelsConfigured, setChannelsConfigured] = React.useState(false);
    const [rolesConfigured, setRolesConfigured] = React.useState(false);

    const [errors, setErrors] = React.useState([] as string[]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [roles, setRoles] = React.useState([] as DiscordRole[]);

    const [showRoleWarning, setShowRoleWarning] = React.useState(false);

    const [isLoadingRoleChanges, setIsLoadingRoleChanges] = React.useState(false);

    let user = useUser();

    React.useEffect(() => {
        getServerDetails();
        getChannels();
        getOrganizationGuild().then((res) => {
            setGuild(res.result);
        });
    }, []);

    React.useEffect(() => {
        setChannelsConfigured(channels.some((c) => c.name.toLowerCase() == "general" || c.name.toLowerCase() == "team 1 voice" || c.name.toLowerCase() == "team 2 voice"));
    }, [channels]);

    React.useEffect(() => {
        const showWarning = (server.roles || []).filter((r) => r.isDefault).length == 0;
        setShowRoleWarning(showWarning);
        setIsDefault(showWarning);
        setChannels(allChannels.filter((c) => c.parent_id && c.parent_id == server.categoryID));
    }, [server, allChannels]);

    const getChannels = () => {
        return listChannels().then((res) => {
            if (res.success) {
                setAllChannels(res.result);
            }
        });
    };

    const getServerDetails = () => {
        setShowLink(false);
        return getOrganizationServer().then((res) => {
            if (res.success) {
                setServer(res.result);
                setRolesConfigured(!!res.result.roles && res.result.roles.some((r) => r.isDefault));
                setIsLoading(false);
            } else {
                listCategories().then((res) => {
                    if (res.success) {
                        setCategories(res.result);
                        setShowLink(true);
                    } else {
                        setErrors(res.errors);
                    }
                    setIsLoading(false);
                });
            }
        });
    };

    const [showManageServerModal, setShowManageServerModal] = React.useState(false);
    const [isLoadingServerChanges, setIsLoadingServerChanges] = React.useState(false);

    const linkCategory = (category: DiscordChannel) => {
        setIsLoadingServerChanges(true);
        linkExistingCategory({ categoryID: category.id } as LinkCategoryRequest).then((res) => {
            if (res.success) {
                getServerDetails().then(() => {
                    getChannels();
                    setIsLoadingServerChanges(false);
                });
            } else {
                setIsLoadingServerChanges(false);
                setErrors(res.errors);
            }
        });
    };

    const create = () => {
        createCategory({} as CreateCategoryRequest).then((res) => {
            if (res.success) {
                getServerDetails().then(() => {
                    getChannels();
                    setIsLoadingServerChanges(false);
                });
            } else {
                setIsLoadingServerChanges(false);
                setErrors(res.errors);
            }
        });
    };

    const [showCreateChannelsModal, setShowCreateChannelsModal] = React.useState(false);

    const startCreateChannels = () => {
        setShowCreateChannelsModal(true);
    };
    const completeCreateChannels = () => {
        createChannels({
            voiceChannels: ["Team 1 Voice", "Team 2 Voice"],
            textChannels: ["General"],
        } as CreateChannelsRequest).then((res) => {
            if (res.success) {
                setShowCreateChannelsModal(false);
                getServerDetails();
            } else {
                setErrors(res.errors);
            }
        });
    };

    const [showManageRolesModal, setShowManageRolesModal] = React.useState(false);
    const [isDefault, setIsDefault] = React.useState(false);

    const startManageRoles = () => {
        listRoles().then((res) => {
            if (res.success) {
                setRoles(res.result);
                if ((server.roles || []).filter((r) => r.isDefault).length == 0) {
                    setIsDefault(true);
                } else {
                    setIsDefault(false);
                }
                setShowManageRolesModal(true);
            } else {
                setErrors(res.errors);
                setShowErrorsModal(true);
            }
        });
    };

    const completeLinkRole = (role: DiscordRole) => {
        setIsLoadingRoleChanges(true);
        linkExistingRole({ roleID: role.id, isDefault: isDefault } as LinkRoleRequest).then((res) => {
            getServerDetails().then(() => {
                setIsLoadingRoleChanges(false);
            });
        });
    };

    const removeRole = (role: DiscordServerRole) => {
        setIsLoadingRoleChanges(true);
        removeServerRole({ roleID: role.roleID, serverID: server.id } as RemoveRoleRequest).then((res) => {
            getServerDetails().then(() => {
                setIsLoadingRoleChanges(false);
            });
        });
    };

    const [showErrorsModal, setShowErrorsModal] = React.useState(false);

    React.useEffect(() => {
        if (errors.length > 0) {
            setShowErrorsModal(true);
        }
    }, [errors]);

    const deleteServer = () => {
        removeOrganizationServer({ serverID: server.id }).then((res) => {
            getServerDetails();
            getChannels();
            getOrganizationGuild().then((res) => {
                setGuild(res.result);
            });
        });
    };

    return (
        <>
            <SkeletonWrapper loading={isLoading}>
                <Box direction="row" gap="small" justify="center" align="center" pad="small">
                    <Image src={`https://cdn.discordapp.com/icons/${guild?.id}/${guild?.icon}`} width="64px" style={{ borderRadius: "10px" }} />
                    <Text size="large">{guild?.name}</Text>
                </Box>
                <Stack guidingChild={1}>
                    <Diagram
                        connections={[
                            {
                                fromTarget: "1",
                                toTarget: "2",
                                thickness: "xsmall",
                                anchor: "horizontal",
                                color: server.id ? "brand" : "background-back",
                                type: "rectilinear",
                            },
                            {
                                fromTarget: "2",
                                toTarget: "3",
                                thickness: "xsmall",
                                anchor: "horizontal",
                                color: channelsConfigured ? "brand" : "background-front",
                                type: "rectilinear",
                            },
                        ]}
                    />
                    <Grid columns={{ count: 3, size: "auto" }} gap="large">
                        <Stack anchor="top-right">
                            <Box id="1" pad="small" background="background" round="small" justify="between" gap="medium">
                                <Box>
                                    <Text size="large">Server</Text>
                                    {server.id && (
                                        <Box>
                                            <Text size="small">
                                                Category <Text color="brand">{server.name}</Text> configured
                                            </Text>
                                            <Button icon={<FontAwesomeIcon icon={["fad", "trash"]}></FontAwesomeIcon>} onClick={deleteServer}></Button>
                                        </Box>
                                    )}
                                </Box>
                                {!server.id && <Button size="small" style={{ borderRadius: "4px" }} label="Manage Server" onClick={() => setShowManageServerModal(true)} />}
                            </Box>
                            <Box pad="small">{server.id && <FontAwesomeIcon color="var(--accent)" icon={["fad", "check-circle"]} />}</Box>
                        </Stack>
                        <Stack anchor="top-right">
                            <Box id="2" pad="small" background="background" round="small" justify="between" gap="medium">
                                <Box>
                                    <Text size="large">Channels</Text>

                                    {server.id && channels.length > 0 && (
                                        <Box>
                                            {channels.map((channel) => (
                                                <Box key={channel.id} direction="row" gap="small" align="center">
                                                    {channel.type == ChannelType.GUILD_VOICE && <FontAwesomeIcon icon={["fad", "microphone-alt"]} />}
                                                    {channel.type == ChannelType.GUILD_TEXT && <FontAwesomeIcon icon={["fad", "message-lines"]} />}
                                                    <Text>{channel.name}</Text>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                                {!channelsConfigured && <Button size="small" style={{ borderRadius: "4px" }} label="Create Channels" onClick={() => startCreateChannels()} />}
                            </Box>
                            <Box pad="small">{channelsConfigured && <FontAwesomeIcon color="var(--accent)" icon={["fad", "check-circle"]} />}</Box>
                        </Stack>
                        <Stack anchor="top-right">
                            <Box id="3" pad="small" background="background" round="small" justify="between" gap="medium">
                                <Box>
                                    <Text size="large">Roles</Text>
                                    {server.roles?.map((role) => (
                                        <Box key={role.roleID} direction="row" justify="between">
                                            <Text>{role.roleName}</Text>
                                            {role.isDefault && <FontAwesomeIcon color="var(--accent)" icon={["fad", "star"]} />}
                                        </Box>
                                    ))}
                                </Box>

                                <Button size="small" style={{ borderRadius: "4px" }} label="Manage roles" onClick={() => startManageRoles()} />
                            </Box>
                            <Box pad="small">{rolesConfigured && <FontAwesomeIcon color="var(--accent)" icon={["fad", "check-circle"]} />}</Box>
                        </Stack>
                    </Grid>
                </Stack>
            </SkeletonWrapper>

            <StyledModal isOpen={showManageServerModal} setIsOpen={() => setShowManageServerModal(false)}>
                <Box pad="small" gap="small" width="medium">
                    <Heading level={3}>Manage Server</Heading>
                    {showLink && (
                        <Box gap="small">
                            <Text>Link existing category</Text>
                            <List
                                pad="xs"
                                data={categories}
                                itemKey="id"
                                primaryKey={(category: DiscordChannel) => (
                                    <Box key={category.id}>
                                        <Text>{category.name}</Text>
                                    </Box>
                                )}
                                action={(item, index) => <Button hoverIndicator key={item.id} size="small" secondary disabled={isLoadingServerChanges} onClick={() => linkCategory(item)} icon={<FontAwesomeIcon icon={["fad", "caret-right"]}></FontAwesomeIcon>} />}
                            ></List>
                            <Box align="center" justify="center">
                                <Text size="medium">OR</Text>
                            </Box>
                            <Box gap="small">
                                <Text>
                                    Create new category for <Text color="brand">{user.organization.name}</Text>
                                </Text>
                                <Box>
                                    <Button primary label="Create" onClick={() => create()} />
                                </Box>
                            </Box>
                        </Box>
                    )}

                    <Box direction="row" justify="between" align="center" gap="small">
                        <Button label="Close" onClick={() => setShowManageServerModal(false)} />
                    </Box>
                </Box>
            </StyledModal>
            <StyledModal isOpen={showCreateChannelsModal} setIsOpen={() => setShowCreateChannelsModal(false)}>
                <Box pad="small" gap="small">
                    <Heading level={3}>Create the following channels?</Heading>
                    <Text>General - Text Channel</Text>
                    <Text>Team 1 Voice - Voice Channel</Text>
                    <Text>Team 2 Voice - Voice Channel</Text>
                    <Box direction="row" justify="between" align="center" gap="small">
                        <Button label="Cancel" onClick={() => setShowCreateChannelsModal(false)} />
                        <Button primary label="Create" onClick={() => completeCreateChannels()} />
                    </Box>
                </Box>
            </StyledModal>
            <StyledModal isOpen={showErrorsModal} setIsOpen={() => setShowErrorsModal(false)}>
                <Box pad="small" gap="small">
                    <Heading level={3}>Errors</Heading>
                    {errors.map((e) => (
                        <Text>{e}</Text>
                    ))}
                    <Box direction="row" justify="between" align="center" gap="small">
                        <Button label="Close" onClick={() => setShowErrorsModal(false)} />
                    </Box>
                </Box>
            </StyledModal>

            <StyledModal isOpen={showManageRolesModal} setIsOpen={() => setShowManageRolesModal(false)}>
                <Box pad="small" gap="small" width="medium">
                    <Heading level={3}>Manage Roles</Heading>
                    {showRoleWarning && <Text color="status-critical">The server requires at least one default role to automatically add new aliases to the server</Text>}
                    <Text>Current roles</Text>
                    {server.roles && (
                        <List
                            pad="xs"
                            data={server.roles}
                            itemKey="roleID"
                            primaryKey={(role: DiscordServerRole) => (
                                <Box key={role.roleID} direction="row" gap="small" align="center">
                                    {role.isDefault && <FontAwesomeIcon color="var(--accent)" icon={["fad", "star"]} />}
                                    <Text>{role.roleName}</Text>
                                </Box>
                            )}
                            action={(item, index) => <Button hoverIndicator key={item.roleID} size="small" secondary disabled={isLoadingRoleChanges} onClick={() => removeRole(item)} icon={<FontAwesomeIcon color="red" icon={["fad", "trash"]}></FontAwesomeIcon>} />}
                        ></List>
                    )}
                    <Text>Available roles</Text>
                    <FormField>
                        <CheckBox label="Make Default" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
                    </FormField>
                    <List
                        pad="xs"
                        data={roles}
                        itemKey="id"
                        primaryKey={(role: DiscordRole) => (
                            <Box key={role.id}>
                                <Text>{role.name}</Text>
                            </Box>
                        )}
                        action={(item, index) => <Button hoverIndicator key={item.id} size="small" secondary disabled={isLoadingRoleChanges} onClick={() => completeLinkRole(item)} icon={<FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon>} />}
                    ></List>
                    <SkeletonWrapper loading={isLoadingRoleChanges}>
                        <></>
                    </SkeletonWrapper>
                    <Box direction="row" justify="between" align="center" gap="small">
                        <Button label="Close" onClick={() => setShowManageRolesModal(false)} />
                    </Box>
                </Box>
            </StyledModal>
        </>
    );
};

export default DiscordServerManagement;
